<template>
  <div class="novel-list-item">
    <router-link class="novel-list-item-link" :to="{ name: 'editor', params: { novelId: novel.novelId } }">
      <!-- サムネイル + ピン -->
      <div class="novel-thumbnail-wrapper">
        <div class="novel-thumbnail">
          <image-view
            class="novel-image"
            :key="coverImageVersion"
            :src="getImageKey"
            placeholder="/img/placeholders/novel.png"
          />
        </div>
        <div class="novel-pin" @click.prevent="onClickPinned">
          <IconPin class="pin-icon" :class="{ active: novel.pinned }" />
        </div>
      </div>

      <!-- 情報 -->
      <div class="novel-info">
        <!-- タイトル -->
        <div class="novel-title line-clamp">{{ novel.title }}</div>

        <!-- ステータス + 日付 -->
        <div class="novel-meta-row">
          <span v-if="workStatusLabel" class="novel-status-badge">{{ workStatusLabel }}</span>
          <span class="novel-date">
            <IconReload class="date-icon" />
            {{ toUpdatedAt }}
          </span>
          <span class="novel-date">
            <IconPencil class="date-icon" />
            {{ toCreatedAt }}
          </span>
        </div>

        <!-- グループ -->
        <div v-if="novel.groups && novel.groups.length > 0" class="novel-groups-row">
          <span v-for="group in novel.groups" :key="group.groupId" class="novel-group-chip">
            <IconFolderHeart class="group-chip-icon" />
            {{ group.name }}
          </span>
        </div>
      </div>
    </router-link>

    <!-- 三点メニュー -->
    <div class="novel-menu-area">
      <button class="novel-menu-button" @click="toggleMenu">
        <IconVerticalEllipsis class="icon-20" />
      </button>

      <!-- ポップオーバーメニュー -->
      <div v-if="isMenuOpen" class="menu-popover">
        <button class="menu-item" @click="showCopyNovelDialog">
          <IconCopy class="menu-item-icon" />
          <span>{{ $t("homePage.novelList.listItem.copyNovel") }}</span>
        </button>
        <button class="menu-item" @click="showGroupingDialog">
          <IconFolderHeart class="menu-item-icon" />
          <span>{{ $t("homePage.novelList.listItem.grouping") }}</span>
        </button>
        <button class="menu-item" @click="importNovel">
          <IconUpload class="menu-item-icon" />
          <span>{{ $t("homePage.novelList.listItem.importManuscript") }}</span>
        </button>
        <button class="menu-item menu-item-danger" @click="deleteNovel">
          <IconTrash class="menu-item-icon" />
          <span>{{ $t("homePage.novelList.listItem.deleteNovel") }}</span>
        </button>
      </div>
    </div>

    <!-- メニュー外クリックで閉じる -->
    <div v-if="isMenuOpen" class="menu-backdrop" @click.stop="toggleMenu" />

    <input style="display: none" ref="input" type="file" accept=".txt,.docx" multiple @change="onSelectFile()" />
  </div>
</template>

<script lang="ts">
import novelItemMixin from "@/mixins/novelItemMixin";
import ImageView from "@/components/atoms/ImageView.vue";
import IconPin from "~icons/figma/Pin";
import IconVerticalEllipsis from "~icons/figma/VerticalEllipsis";
import IconCopy from "~icons/figma/Copy";
import IconUpload from "~icons/figma/Upload";
import IconTrash from "~icons/figma/Trash";
import IconFolderHeart from "~icons/figma/FolderHeart";
import IconReload from "~icons/figma/Reload";
import IconPencil from "~icons/figma/Pencil";

export default novelItemMixin.extend({
  components: {
    ImageView,
    IconPin,
    IconVerticalEllipsis,
    IconCopy,
    IconUpload,
    IconTrash,
    IconFolderHeart,
    IconReload,
    IconPencil,
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

$menu-button-offset: 36px;

.novel-list-item {
  position: relative;
  min-width: 0;
  border-bottom: 1px solid var(--color-border);
}

/* アイコンサイズユーティリティ */
.icon-20 {
  font-size: 20px;
}

.novel-list-item-link {
  @include hover-overlay;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 14px $menu-button-offset 14px 0;
  text-decoration: none;
  color: var(--color-text-normal);
}

/* サムネイル + ピン */
.novel-thumbnail-wrapper {
  position: relative;
  flex-shrink: 0;
}

.novel-thumbnail {
  width: 64px;
  height: 90px;
  overflow: hidden;
}

.novel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.novel-pin {
  position: absolute;
  bottom: 4px;
  left: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-sm);
  cursor: pointer;

  .pin-icon {
    font-size: var(--font-size-md);
    color: var(--color-text-low);

    &.active {
      color: var(--color-warning);
    }
  }
}

/* 情報エリア */
.novel-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
  align-self: stretch;
}

.novel-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  line-height: var(--line-height-default);
}

.line-clamp {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* ステータス + 日付行 */
.novel-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
}

.novel-status-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: var(--font-size-xxs);
  line-height: 14px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-invert);
  color: var(--color-text-invert);
  white-space: nowrap;
  text-align: center;
}

.novel-date {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: var(--font-size-xs);
  line-height: 1;
  color: var(--color-text-low);
  white-space: nowrap;

  .date-icon {
    font-size: 13px;
    flex-shrink: 0;
  }
}

/* グループ行 */
.novel-groups-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
  min-width: 0;

  @include mq(sp) {
    flex-wrap: nowrap;
    margin-right: calc(-1 * #{$menu-button-offset} - var(--novel-list-padding-x, 0px));
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.novel-group-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 2px 8px;
  font-size: var(--font-size-xxs);
  line-height: 14px;
  color: var(--color-text-normal);
  background-color: var(--color-surface-base);
  border-radius: var(--radius-rounded);
  white-space: nowrap;

  .group-chip-icon {
    font-size: var(--font-size-xs);
    flex-shrink: 0;
  }
}

/* 三点メニューボタン */
.novel-menu-area {
  position: absolute;
  right: 4px;
  top: 14px;
}

.novel-menu-button {
  @include hover-overlay;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-low);
  border-radius: var(--radius-rounded);
}

/* ポップオーバーメニュー */
.menu-popover {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 4px 0;
  z-index: 10;
  white-space: nowrap;
}

.menu-item {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;
  font-size: var(--font-size-xs);


  &.menu-item-danger {
    color: var(--color-warning);
  }
}

.menu-item-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

/* メニュー外クリック用の透明背景 */
.menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
</style>
